/**
 * Main composable for VulForm Vue integration
 */

import { ref, computed, onMounted, watch } from 'vue';
import {
  VulFormApiClient,
  VulFormError,
  FormTemplate,
  ValidationErrors,
  SubmissionResponse,
  FormValidator,
} from '@vulform/core';

interface UseVulFormProps {
  templateId: string;
  apiKey?: string;
  apiUrl?: string;
  prefill?: Record<string, any>;
  onSuccess?: (response: SubmissionResponse) => void;
  onError?: (error: VulFormError) => void;
  onValidationError?: (errors: ValidationErrors) => void;
  enableAnalytics?: boolean;
  spamProtection?: boolean;
}

export function useVulForm(props: UseVulFormProps) {
  const {
    templateId,
    apiKey,
    apiUrl,
    prefill = {},
    onSuccess,
    onError,
    onValidationError,
    enableAnalytics = true,
    spamProtection = true,
  } = props;

  // State
  const template = ref<FormTemplate | null>(null);
  const loading = ref(true);
  const error = ref<VulFormError | null>(null);
  const submitting = ref(false);
  const submitted = ref(false);
  const formData = ref<Record<string, any>>({ ...prefill });
  const validationErrors = ref<ValidationErrors>({});

  // API client
  const apiClient = ref<VulFormApiClient | null>(null);

  // Initialize API client
  const initializeClient = () => {
    // Enhanced API key resolution with better environment variable support
    const resolvedApiKey =
      apiKey ||
      import.meta.env.VITE_VULFORM_API_KEY ||
      process.env.VUE_APP_VULFORM_API_KEY ||
      process.env.VULFORM_API_KEY;

    if (!resolvedApiKey) {
      error.value = new VulFormError(
        'MISSING_API_KEY',
        'API key is required. Set VITE_VULFORM_API_KEY, VUE_APP_VULFORM_API_KEY, or pass apiKey prop.'
      );
      loading.value = false;
      return;
    }

    try {
      const client = new VulFormApiClient({
        apiKey: resolvedApiKey,
        baseUrl: apiUrl, // Let the API client handle URL resolution
        debug: Boolean(import.meta.env?.DEV),
      });
      apiClient.value = client;
    } catch (err) {
      error.value =
        err instanceof VulFormError
          ? err
          : new VulFormError('CLIENT_INIT_ERROR', 'Failed to initialize API client');
      loading.value = false;
    }
  };

  // Load template
  const loadTemplate = async () => {
    if (!apiClient.value || !templateId) return;

    try {
      loading.value = true;
      error.value = null;

      const templateData = await apiClient.value.getTemplate(templateId);
      template.value = templateData;

      // Set default values from template
      const defaults: Record<string, any> = {};
      templateData.fields.forEach(field => {
        if (field.defaultValue !== undefined) {
          defaults[field.name] = field.defaultValue;
        }
      });

      formData.value = { ...defaults, ...formData.value };

      // Track form view
      if (enableAnalytics && spamProtection) {
        apiClient.value.trackEvent({
          type: 'form_view',
          templateId,
          timestamp: Date.now(),
        });
      }
    } catch (err) {
      const errorObj =
        err instanceof VulFormError
          ? err
          : new VulFormError('LOAD_FAILED', 'Failed to load form template');
      error.value = errorObj;
      onError?.(errorObj);
    } finally {
      loading.value = false;
    }
  };

  // Update individual field
  const updateField = (fieldName: string, value: any) => {
    formData.value = { ...formData.value, [fieldName]: value };

    // Clear field error when user starts typing
    if (validationErrors.value[fieldName]) {
      const newErrors = { ...validationErrors.value };
      delete newErrors[fieldName];
      validationErrors.value = newErrors;
    }
  };

  // Validate form
  const validateForm = async (): Promise<boolean> => {
    if (!template.value) return false;

    const errors = FormValidator.validateForm(formData.value, template.value);
    validationErrors.value = errors;

    const isValid = Object.keys(errors).length === 0;

    if (!isValid) {
      onValidationError?.(errors);

      // Track validation errors
      if (enableAnalytics && apiClient.value) {
        apiClient.value.trackEvent({
          type: 'validation_error',
          templateId,
          timestamp: Date.now(),
          metadata: { errorFields: Object.keys(errors) },
        });
      }
    }

    return isValid;
  };

  // Submit form
  const submitForm = async (data?: Record<string, any>) => {
    if (!apiClient.value || !template.value) return;

    const submitData = data || formData.value;
    submitting.value = true;
    error.value = null;

    try {
      // Validate first
      const isValid = await validateForm();
      if (!isValid) {
        return;
      }

      // Track submission start
      if (enableAnalytics) {
        apiClient.value.trackEvent({
          type: 'form_submit',
          templateId,
          timestamp: Date.now(),
          metadata: { fieldCount: Object.keys(submitData).length },
        });
      }

      // Submit
      const response = await apiClient.value.submitForm({
        templateId,
        data: submitData,
      });

      submitted.value = true;
      onSuccess?.(response);
    } catch (err) {
      const errorObj =
        err instanceof VulFormError
          ? err
          : new VulFormError('SUBMIT_FAILED', 'Form submission failed');
      error.value = errorObj;
      onError?.(errorObj);
    } finally {
      submitting.value = false;
    }
  };

  // Reset form
  const resetForm = () => {
    formData.value = { ...prefill };
    validationErrors.value = {};
    submitted.value = false;
    error.value = null;
  };

  // Clear errors
  const clearErrors = () => {
    validationErrors.value = {};
    error.value = null;
  };

  // Initialize on mount
  onMounted(() => {
    initializeClient();
  });

  // Watch for client changes to load template
  watch(
    apiClient,
    newClient => {
      if (newClient) {
        loadTemplate();
      }
    },
    { immediate: true }
  );

  // Watch for templateId changes
  watch(
    () => templateId,
    newTemplateId => {
      if (newTemplateId && apiClient.value) {
        loadTemplate();
      }
    }
  );

  return {
    // State
    template: computed(() => template.value),
    loading: computed(() => loading.value),
    error: computed(() => error.value),
    submitting: computed(() => submitting.value),
    submitted: computed(() => submitted.value),
    formData: computed(() => formData.value),
    validationErrors: computed(() => validationErrors.value),

    // Actions
    updateField,
    validateForm,
    submitForm,
    resetForm,
    clearErrors,
  };
}

export default useVulForm;
